import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { DateTime } from 'luxon';

import styled from '@emotion/styled';

import { useApolloClient } from '@apollo/client/react';

import {
  Box,
  Button,
  COLORS,
  FontWeight,
  Input,
  Text,
  UnstyledButton,
} from '@clutter/clean';

import {
  CouponDocument,
  CouponFragment,
  CouponQuery,
  CouponQueryVariables,
} from '@graphql/platform';

import {
  CHALLENGER_PROMO_GENERIC_ERROR,
  challengerPromoErrorMessage,
  challengerPromoSuccessMessage,
  isAppointmentCoupon,
} from '@root/components/checkout/cart/challenger_promo';
import { CouponSummary } from '@root/components/checkout/cart/coupon_summary';
import { useSharedCheckoutContext } from '@root/components/checkout/context';
import { CHALLENGER as T } from '@root/components/checkout/product_pages/challenger/tokens';
import { track } from '@root/initializers/wt';

import { useClientDataContext } from '@shared/client_data_context';

import { FormattedGQLError, parseGQLErrorUnion } from '@utils/gql_errors';
import { useOnMount } from '@utils/hooks/mount';
import { useBreakpoints } from '@utils/hooks/use_breakpoints';
import { extractRateGroup } from '@utils/pricing/pricing_set';

import type { SharedCheckoutDateScheduled } from '../data';

const InputContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  input {
    flex-grow: 1;
  }
`;

type ChallengerFieldState = 'default' | 'error' | 'success';

/** Figma 707:23689 / 18838 / 19022 / 19398 — max 364px field, Apply 48px inside. */
const ChallengerField = styled.div<{ $state: ChallengerFieldState }>`
  display: flex;
  align-items: center;
  gap: ${T.space[16]};
  width: 100%;
  max-width: 364px;
  min-width: 220px;
  padding: ${T.space[4]} ${T.space[4]} ${T.space[4]} ${T.space[16]};
  box-sizing: border-box;
  border: 1px solid
    ${({ $state }) =>
      $state === 'error'
        ? T.color.textError
        : $state === 'success'
          ? T.color.borderBrand
          : T.color.borderDefault};
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};

  input {
    flex: 1 1 auto;
    min-width: 0;
    height: 48px;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent !important;
    padding: 0 !important;
    font-family: ${T.font.sans};
    font-size: 16px;
    font-weight: ${FontWeight.Medium};
    line-height: 24px;
    color: ${T.color.textPrimary};

    &::placeholder {
      font-weight: ${FontWeight.Regular};
      color: ${T.color.textTertiary};
    }

    &:disabled {
      opacity: 1;
      color: ${T.color.textPrimary};
    }
  }
`;

const ChallengerApply = styled.button<{
  $active?: boolean;
  disabled?: boolean;
}>`
  appearance: none;
  flex-shrink: 0;
  height: 48px;
  padding: 0 ${T.space[24]};
  border: none;
  border-radius: ${T.radius.sm};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ $active, disabled }) =>
    disabled || !$active ? 'rgba(154, 161, 165, 0.2)' : T.color.textBrand};
  color: ${({ $active, disabled }) =>
    disabled || !$active ? T.color.textDisabled : T.color.textInverse};
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const ChallengerCaption = styled.p<{ $tone: 'error' | 'success' }>`
  margin: 0;
  max-width: 364px;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${({ $tone }) =>
    $tone === 'error' ? T.color.textError : T.color.textBrandDark};
`;

/** Funnel-Feedback 29:3770 — title + field, gap 24. */
const ChallengerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
  width: 100%;
`;

const ChallengerTitle = styled.h2`
  margin: 0;
  width: 100%;
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textPrimary};
`;

const ChallengerFieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[4]};
  width: 100%;
  max-width: 364px;
`;

const PromoCodeInput: React.FC<{
  coupon?: CouponFragment;
  service?: 'storage' | 'moving';
  disabled?: boolean;
  /** Challenger confirm page (Figma handoff) chrome. */
  appearance?: 'classic' | 'challenger';
  /** Storage + protection (pre-tax) used for storage promo success copy / % discounts. */
  monthlyBaseAmount?: number;
  /** Pick-up fees total used for appointment promo success copy / % discounts. */
  pickupFeeAmount?: number;
  onChange(value: CouponFragment | undefined): void;
}> = ({
  disabled,
  coupon,
  service,
  appearance = 'classic',
  monthlyBaseAmount = 0,
  pickupFeeAmount = 0,
  onChange,
}) => {
  const { isDesktop } = useBreakpoints();
  const {
    flowState: {
      values: { dateScheduled, commitment },
    },
    pricingSet,
  } = useSharedCheckoutContext();
  const rateGroupId = useMemo(
    () => extractRateGroup(commitment, pricingSet)?.id,
    [commitment, pricingSet],
  );
  const {
    data: { urlPromoCode },
  } = useClientDataContext();
  const [inputValue, setInputValue] = useState(
    coupon?.promoCode ?? urlPromoCode ?? '',
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CouponQuery['coupon'] | undefined>();
  const [systemError, setSystemError] = useState<string | undefined>();
  const client = useApolloClient();

  const orderBeforeCutoff = (
    scheduled: SharedCheckoutDateScheduled | undefined,
    couponValue: CouponFragment | undefined,
  ) => {
    if (!couponValue?.scheduledCutoff || !scheduled) return true;

    const cutoffDate = DateTime.fromISO(couponValue.scheduledCutoff).endOf(
      'day',
    );
    const scheduledDate = DateTime.fromISO(scheduled.fromTime);

    return scheduledDate < cutoffDate;
  };

  const validateCoupon = async (code: string, serviceName?: string) => {
    try {
      setLoading(true);
      setSystemError(undefined);
      const { data } = await client.query<CouponQuery, CouponQueryVariables>({
        query: CouponDocument,
        variables: { code, service: serviceName, rateGroupId },
      });

      if (data) {
        setResult(data.coupon);
        if (
          data.coupon.__typename === 'Coupon' &&
          orderBeforeCutoff(dateScheduled, data.coupon)
        ) {
          onChange(data.coupon);
        } else {
          onChange(undefined);
        }
      }
    } catch {
      setResult(undefined);
      onChange(undefined);
      setSystemError(CHALLENGER_PROMO_GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Only mirror applied coupons into the field. Clearing `coupon` after a validation
  // error must not wipe the typed code (challenger payment promo behavior).
  useEffect(() => {
    if (coupon?.promoCode) {
      setInputValue(coupon.promoCode);
    }
  }, [coupon]);

  useOnMount(() => {
    if (urlPromoCode && !coupon) {
      validateCoupon(urlPromoCode, service);
    }
  });

  const [resultCoupon, couponError] = parseGQLErrorUnion(result);
  const showCutoffError =
    !!resultCoupon && !orderBeforeCutoff(dateScheduled, resultCoupon);

  const applyPromo = async () => {
    validateCoupon(inputValue || '', service);
    track({
      objectType: 'button',
      objectName: 'apply_promo',
      action: 'click',
      label: 'promo_code',
    });
  };

  const canApply = !!inputValue?.trim() && !loading && !disabled;

  if (appearance === 'challenger') {
    const fieldState: ChallengerFieldState = coupon
      ? 'success'
      : couponError || showCutoffError || systemError
        ? 'error'
        : 'default';
    const errorText =
      systemError ||
      challengerPromoErrorMessage(couponError, {
        showCutoffError,
        scheduledCutoff: resultCoupon?.scheduledCutoff,
      });
    const promoBaseAmount = coupon
      ? isAppointmentCoupon(coupon)
        ? pickupFeeAmount
        : monthlyBaseAmount
      : undefined;
    const successText =
      coupon && promoBaseAmount != null && promoBaseAmount > 0
        ? challengerPromoSuccessMessage(coupon, promoBaseAmount)
        : undefined;

    return (
      <ChallengerStack>
        <ChallengerTitle>Your potential savings</ChallengerTitle>
        <ChallengerFieldStack>
          <ChallengerField $state={fieldState}>
            <Input
              value={inputValue ?? ''}
              type="text"
              placeholder="Promo code"
              onChange={(event) => {
                const next = event.target.value || '';
                setInputValue(next);
                setResult(undefined);
                setSystemError(undefined);
                if (coupon) {
                  onChange(undefined);
                }
              }}
              maxLength={15}
              disabled={disabled}
              aria-label="Promo code"
              aria-invalid={fieldState === 'error' || undefined}
            />
            <ChallengerApply
              type="button"
              $active={canApply}
              onClick={applyPromo}
              disabled={!canApply}
            >
              {loading ? '…' : 'Apply'}
            </ChallengerApply>
          </ChallengerField>
          {errorText && (
            <ChallengerCaption $tone="error">{errorText}</ChallengerCaption>
          )}
          {successText && !errorText && (
            <ChallengerCaption $tone="success">{successText}</ChallengerCaption>
          )}
        </ChallengerFieldStack>
      </ChallengerStack>
    );
  }

  return (
    <Box>
      <Text.Body weight={FontWeight.Medium}>Promo code</Text.Body>
      <InputContainer>
        <Input
          value={inputValue ?? ''}
          type="text"
          onChange={(event) => {
            setInputValue(event.target.value || '');
            setResult(undefined);
            setSystemError(undefined);
          }}
          maxLength={15}
          disabled={disabled}
          state={couponError ? 'error' : undefined}
        />
        <Button
          onClick={applyPromo}
          kind="secondary"
          size="medium"
          loading={loading}
          disabled={disabled || loading}
        >
          Apply
        </Button>
      </InputContainer>
      <Box padding="8px 0 12px">
        {couponError && (
          <Text.Caption color={COLORS.toucan} weight={FontWeight.Medium}>
            <FormattedGQLError error={couponError} />
          </Text.Caption>
        )}
        {showCutoffError && (
          <Text.Caption color={COLORS.toucan} weight={FontWeight.Medium}>
            This promo code is only eligible for appointments before{' '}
            {resultCoupon?.scheduledCutoff}
          </Text.Caption>
        )}
        {systemError && (
          <Text.Caption color={COLORS.toucan} weight={FontWeight.Medium}>
            {systemError}
          </Text.Caption>
        )}
      </Box>
      {!isDesktop && coupon && (
        <Box.Flex justifyContent="space-between">
          <Box>
            <Text.Callout weight={FontWeight.Medium} color={COLORS.tealPrimary}>
              <CouponSummary coupon={coupon} />
            </Text.Callout>
          </Box>
          <UnstyledButton
            onClick={() => {
              setResult(undefined);
              setInputValue('');
              onChange(undefined);
            }}
          >
            <Text.SmallCaps color={COLORS.hippo}>remove</Text.SmallCaps>
          </UnstyledButton>
        </Box.Flex>
      )}
    </Box>
  );
};

export { PromoCodeInput };
